/**
 * Workspace key (WK) derivation. Mirrors Go `personalspace.DeriveResourceDEK` /
 * `hkdfDeriveKey`: HKDF-SHA256(ikm = MK, salt = nil, info = workspaceId, len = 32).
 *
 * A nil salt in Go equals a HashLen zero salt, which is what WebCrypto uses for an
 * empty salt, so the outputs are identical (verified by vectors.json).
 */
import { bs } from "./bytes";

/** Derive the AES-KW workspace key. Result is NON-extractable (wrapKey/unwrapKey only). */
export async function deriveWorkspaceKey(
  masterKey: Uint8Array,
  workspaceId: string,
  keyVersion = 1
): Promise<CryptoKey> {
  // v1 keeps the Go-verified info (= workspaceId); later versions (rotation) append the
  // version so each rotation yields an independent key that the MK can always re-derive.
  const info = keyVersion > 1 ? `${workspaceId}#v${keyVersion}` : workspaceId;
  const ikm = await crypto.subtle.importKey(
    "raw",
    bs(masterKey),
    "HKDF",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: new Uint8Array(0),
      info: new TextEncoder().encode(info),
    },
    ikm,
    { name: "AES-KW", length: 256 },
    false,
    ["wrapKey", "unwrapKey"]
  );
}

/**
 * Derive a 32-byte raw HKDF output. Only for interop/tests (extractable by nature);
 * production code must use {@link deriveWorkspaceKey}.
 */
export async function deriveRawBytes(
  masterKey: Uint8Array,
  info: string,
  length = 32
): Promise<Uint8Array> {
  const ikm = await crypto.subtle.importKey(
    "raw",
    bs(masterKey),
    "HKDF",
    false,
    ["deriveBits"]
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: new Uint8Array(0),
      info: new TextEncoder().encode(info),
    },
    ikm,
    length * 8
  );
  return new Uint8Array(bits);
}
